/**
 * SortableCollapsibleCard — collapsed-by-default item card with:
 *   • A summary line ("Hotel Palm · Phuentsholing · 26/05 → 27/05")
 *   • Click to expand body. Only ONE expanded at a time inside a group
 *     (parent manages `expandedId`).
 *   • Drag handle (grip icon) to reorder via @dnd-kit.
 *   • Delete button always visible in the header.
 *
 * Used by Hotels / Activities / Transport / Guides / Day cards on the
 * Tour Detail edit view — replaces the endless vertical scroll the team
 * suffered when a tour had 8+ items each with 12+ fields.
 */
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronRight, GripVertical, Trash2 } from 'lucide-react';
import { Button } from './ui/button';

export const SortableCollapsibleCard = ({
  id,
  index,
  title,
  subtitle,
  expanded,
  onToggle,
  onRemove,
  children,
  badge,
  testid,
}) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-md bg-card ${isDragging ? 'shadow-lg ring-2 ring-amber-300' : ''}`}
      data-testid={testid || `sortable-card-${id}`}
    >
      <div className="flex items-center gap-1 p-2">
        {/* Drag handle — only this small area listens for drag, so taps
            elsewhere on the row don't accidentally trigger a drag. */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="touch-none cursor-grab active:cursor-grabbing p-1 text-muted-foreground hover:text-foreground"
          aria-label="Drag to reorder"
          data-testid={`drag-${id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        {/* Summary line — tap anywhere here to expand/collapse */}
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 flex items-center gap-2 text-left min-w-0 py-1"
          data-testid={`toggle-${id}`}
        >
          {expanded
            ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
            : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
          <span className="flex-1 min-w-0">
            <span className="text-sm font-medium truncate block">{title || `Item ${index + 1}`}</span>
            {subtitle && (
              <span className="text-xs text-muted-foreground truncate block">{subtitle}</span>
            )}
          </span>
          {badge}
        </button>
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive shrink-0"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            aria-label="Delete"
            data-testid={`remove-${id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
      {expanded && (
        <div className="border-t p-3 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
};

export default SortableCollapsibleCard;
